import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Store, Euro } from "lucide-react";
import { toast } from "sonner";

export default function BusinessSubsCeoPanel() {
  const [metrics, setMetrics] = useState(null);
  const [plans, setPlans] = useState(null);

  const load = () => {
    api.get("/ceo/business-subscriptions/metrics").then(({ data }) => setMetrics(data)).catch(() => {});
    api.get("/ceo/business-plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const savePlans = async () => {
    const payload = {};
    plans.forEach((p) => { payload[p.id] = { price_month: parseFloat(p.price_month) || 0, price_year: parseFloat(p.price_year) || 0, trial_days: parseInt(p.trial_days) || 0, commission_discount: parseFloat(p.commission_discount) || 0 }; });
    try { await api.put("/ceo/business-plans", { plans: payload }); toast.success("Piani aggiornati"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setField = (id, k, v) => setPlans((ps) => ps.map((p) => p.id === id ? { ...p, [k]: v } : p));

  const eur = (v) => "€" + Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const m = metrics || {};

  return (
    <div className="space-y-5" data-testid="business-subs-ceo-panel">
      <div>
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><Store className="h-5 w-5 text-[#00E676]" /> Fuma Business — Abbonamenti B2B</h2>
        <p className="text-sm text-zinc-500">Ricavi ricorrenti dalle attività: MRR, ARR, conversione e distribuzione per piano.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-gradient-to-br from-[#00E676]/15 to-transparent border border-[#00E676]/30 p-4" data-testid="b2b-mrr"><div className="text-xs uppercase tracking-widest text-[#00E676]">MRR</div><div className="text-2xl font-black mt-1">{eur(m.mrr)}</div></div>
        <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4" data-testid="b2b-arr"><div className="text-xs uppercase tracking-widest text-zinc-500">ARR</div><div className="text-2xl font-black mt-1">{eur(m.arr)}</div></div>
        <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4"><div className="text-xs uppercase tracking-widest text-zinc-500">Conversione a pagamento</div><div className="text-2xl font-black mt-1">{m.conversion_rate ?? 0}%</div></div>
        <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4"><div className="text-xs uppercase tracking-widest text-zinc-500">ARPA</div><div className="text-2xl font-black mt-1">{eur(m.arpa)}</div></div>
      </div>

      <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5">
        <div className="text-sm font-semibold mb-3">Distribuzione per piano</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(m.counts || {}).map(([pid, cnt]) => (
            <div key={pid} className="rounded-xl bg-[#0A0A0C] border border-white/10 p-3" data-testid={`b2b-count-${pid}`}>
              <div className="font-semibold capitalize">{pid}</div>
              <div className="text-2xl font-black">{cnt}</div>
              <div className="text-xs text-zinc-500">GMV {eur((m.gmv_per_plan || {})[pid])} · Rev {eur((m.revenue_per_plan || {})[pid])}</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-zinc-500 mt-2">{m.total_businesses || 0} attività totali · {m.paid || 0} a pagamento · {m.trialing || 0} in prova</div>
      </div>

      {plans && (
        <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Euro className="h-4 w-4 text-[#00E676]" /> Configurazione piani (prezzi CEO-editabili)</div>
          <div className="space-y-3">
            {plans.map((p) => (
              <div key={p.id} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end border-t border-white/5 pt-3" data-testid={`b2b-plan-cfg-${p.id}`}>
                <div className="font-semibold self-center">{p.name}</div>
                <div><Label className="text-xs">€/mese</Label><Input type="number" step="0.5" value={p.price_month} onChange={(e) => setField(p.id, "price_month", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
                <div><Label className="text-xs">€/anno</Label><Input type="number" step="1" value={p.price_year} onChange={(e) => setField(p.id, "price_year", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
                <div><Label className="text-xs">Prova (gg)</Label><Input type="number" value={p.trial_days} onChange={(e) => setField(p.id, "trial_days", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
                <div><Label className="text-xs">Sconto comm. (0-1)</Label><Input type="number" step="0.01" value={p.commission_discount} onChange={(e) => setField(p.id, "commission_discount", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
              </div>
            ))}
          </div>
          <Button data-testid="b2b-save-plans" onClick={savePlans} className="bg-[#00E676] text-black rounded-md mt-4">Salva piani</Button>
        </div>
      )}
    </div>
  );
}
